import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { playerFetch } from '@/api/client'
import type {
  MultipleDeviceGroup,
  MultipleDeviceQuery,
  MultipleDeviceResult,
} from '@/features/logs/multipleDeviceTypes'

/** Player-API `POST /v1/admin/member-device/getPlayersWithSameDeviceIds` request. */
export type GetPlayersWithSameDeviceIdsRequest = {
  page?: number
  pageSize?: number
  deviceId?: string
  player?: string
}

export type SharedDevicePlayerApi = {
  userId: string
  username: string
  accessLevel: string
  siteUrl?: string
  isCheater?: boolean
}

export type SharedDeviceGroupApi = {
  deviceId: string
  playerCount: number
  players: SharedDevicePlayerApi[]
}

/** Winner-shaped envelope from Player-API admin member-device. */
export type GetPlayersWithSameDeviceIdsResponse = {
  value: boolean
  data: {
    results: SharedDeviceGroupApi[]
    totalCount: number
    page: number
    pageSize: number
  }
}

/**
 * Lab `memberDevice/getPlayersWithSameDeviceIds` → Player-API admin.
 * Uses `users.device_ids` (web fingerprint + native device id from saveDeviceId).
 */
export async function getPlayersWithSameDeviceIds(
  body: GetPlayersWithSameDeviceIdsRequest = {},
): Promise<GetPlayersWithSameDeviceIdsResponse> {
  return playerFetch<GetPlayersWithSameDeviceIdsResponse>(
    '/v1/admin/member-device/getPlayersWithSameDeviceIds',
    {
      method: 'POST',
      body: JSON.stringify({
        page: body.page ?? 1,
        pageSize: body.pageSize ?? DEFAULT_PAGE_SIZE,
        deviceId: body.deviceId?.trim() || undefined,
        player: body.player?.trim() || undefined,
      }),
    },
  )
}

/** Map Player-API shared-device groups → console Multiple Device Ids rows. */
export function toMultipleDeviceResult(
  live: GetPlayersWithSameDeviceIdsResponse,
): MultipleDeviceResult {
  const page = live.data?.page ?? 1
  const pageSize = live.data?.pageSize ?? DEFAULT_PAGE_SIZE
  const total = live.data?.totalCount ?? 0
  const rows: MultipleDeviceGroup[] = (live.data?.results ?? []).map((row) => ({
    id: row.deviceId,
    deviceId: row.deviceId,
    playerCount: row.playerCount,
    players: (row.players ?? []).map((p) => ({
      username: p.username,
      accessLevel: p.accessLevel || 'Player',
      siteUrl: p.siteUrl,
      isCheater: Boolean(p.isCheater),
    })),
  }))
  return { rows, total, page, pageSize }
}

/** Re-export query shape for callers that already use MultipleDeviceQuery. */
export type { MultipleDeviceQuery }
